import React, { useState } from 'react';
import {
  Sparkles,
  Save,
  CheckCircle2,
  AlertCircle,
  X,
  FileCode,
  ArrowRight,
} from 'lucide-react';
import { useReconciliation } from '../../context/ReconciliationContext';
import { RazorpayColumnMapping, ErpColumnMapping } from '../../types/reconciliation';

interface ColumnMappingWizardProps {
  onClose: () => void;
}

export const ColumnMappingWizard: React.FC<ColumnMappingWizardProps> = ({ onClose }) => {
  const {
    razorpayInfo,
    erpInfo,
    rpMapping,
    erpMapping,
    updateRpMapping,
    updateErpMapping,
  } = useReconciliation();

  const [savedSuccess, setSavedSuccess] = useState(false);
  const [profileName, setProfileName] = useState('Standard SchemePay Mapping');

  const rpHdrs = razorpayInfo ? razorpayInfo.headers : Object.values(rpMapping);
  const erpHdrs = erpInfo ? erpInfo.headers : Object.values(erpMapping);

  const rpFields: { key: keyof RazorpayColumnMapping; label: string; required: boolean; description: string }[] = [
    { key: 'paymentId', label: 'Payment ID (Primary Key)', required: true, description: 'Unique payment identifier from Razorpay (e.g. pay_xxx or RP001)' },
    { key: 'amount', label: 'Payment Amount', required: true, description: 'Gross captured transaction amount received from customer' },
    { key: 'customerName', label: 'Customer Name', required: false, description: 'Name of the scheme investor / customer' },
    { key: 'paymentDate', label: 'Payment Date & Time', required: false, description: 'Timestamp when payment was captured' },
    { key: 'status', label: 'Transaction Status', required: false, description: 'Gateway status (captured, refunded, failed)' },
    { key: 'refundAmount', label: 'Refund Amount', required: false, description: 'Amount refunded back to customer, if any' },
    { key: 'refundDate', label: 'Refund Date', required: false, description: 'Date when refund was initiated' },
  ];

  const erpFields: { key: keyof ErpColumnMapping; label: string; required: boolean; description: string }[] = [
    { key: 'paymentId', label: 'Payment ID / Link Reference', required: true, description: 'The Razorpay payment ID tagged to the ERP invoice' },
    { key: 'billNumber', label: 'Bill / Invoice Number', required: true, description: 'ERP generated invoice or voucher number' },
    { key: 'billAmount', label: 'Bill Amount', required: true, description: 'Amount of this specific ERP bill' },
    { key: 'customerName', label: 'Customer / Party Name', required: false, description: 'Customer or account name in ERP ledger' },
    { key: 'postingDate', label: 'Posting / Invoice Date', required: false, description: 'Date the bill was posted in the accounting system' },
  ];

  const handleSaveProfile = async () => {
    try {
      await fetch('/api/mappings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profileName,
          razorpayMapping: rpMapping,
          erpMapping: erpMapping,
        }),
      }).catch(() => {});

      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
    } catch {
      setSavedSuccess(true);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col my-8 animate-scale-in">
        {/* Modal Header */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Dynamic Column Mapping Wizard
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Match column headers from your uploaded spreadsheets to SchemePay reconciliation attributes.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div className="p-6 overflow-y-auto space-y-8">
          {/* Razorpay Mapping Section */}
          <div>
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100 dark:border-slate-800">
              <span className="w-2.5 h-2.5 rounded-full bg-brand-500" />
              <h3 className="font-bold text-sm text-slate-900 dark:text-white">
                1. Razorpay Column Mapping
              </h3>
              {razorpayInfo && (
                <span className="text-xs text-slate-500 font-mono">
                  ({razorpayInfo.fileName})
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rpFields.map((field) => {
                const currentVal = rpMapping[field.key];
                const isMatched = !!currentVal && rpHdrs.includes(currentVal);

                return (
                  <div
                    key={field.key}
                    className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1">
                        {field.label}
                        {field.required && <span className="text-rose-500">*</span>}
                      </label>
                      {isMatched ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                          <CheckCircle2 className="w-3 h-3" /> Auto-mapped
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                          <AlertCircle className="w-3 h-3" /> Select column
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2">
                      {field.description}
                    </p>
                    <select
                      value={currentVal || ''}
                      onChange={(e) => updateRpMapping(field.key, e.target.value)}
                      className="w-full text-xs font-medium px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="">-- Choose Column --</option>
                      {rpHdrs.map((hdr) => (
                        <option key={hdr} value={hdr}>
                          {hdr}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ERP Mapping Section */}
          <div>
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100 dark:border-slate-800">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <h3 className="font-bold text-sm text-slate-900 dark:text-white">
                2. ERP Billing Column Mapping
              </h3>
              {erpInfo && (
                <span className="text-xs text-slate-500 font-mono">
                  ({erpInfo.fileName})
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {erpFields.map((field) => {
                const currentVal = erpMapping[field.key];
                const isMatched = !!currentVal && erpHdrs.includes(currentVal);

                return (
                  <div
                    key={field.key}
                    className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1">
                        {field.label}
                        {field.required && <span className="text-rose-500">*</span>}
                      </label>
                      {isMatched ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                          <CheckCircle2 className="w-3 h-3" /> Auto-mapped
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                          <AlertCircle className="w-3 h-3" /> Select column
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2">
                      {field.description}
                    </p>
                    <select
                      value={currentVal || ''}
                      onChange={(e) => updateErpMapping(field.key, e.target.value)}
                      className="w-full text-xs font-medium px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="">-- Choose Column --</option>
                      {erpHdrs.map((hdr) => (
                        <option key={hdr} value={hdr}>
                          {hdr}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-850 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <input
              type="text"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              placeholder="Preset profile name..."
              className="text-xs px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
            />
            <button
              onClick={handleSaveProfile}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-xs font-semibold text-slate-800 dark:text-slate-200 shadow-sm transition"
            >
              <Save className="w-3.5 h-3.5 text-brand-600" />
              <span>{savedSuccess ? 'Saved to Cloudflare!' : 'Save Preset'}</span>
            </button>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold shadow-md transition"
            >
              Apply & Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
